import { useState, useEffect, useCallback, useRef } from "react";
import useViewPreference from '../hooks/useViewPreference';
import axios from "axios";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { MessageSquare, Loader2, Clock, User, Ticket, Search, Eye, Download, Send, X, FileText, Image, File, Paperclip, CheckCircle, AlertCircle, XCircle, Pencil, Upload, CheckCircle2, ArrowRightLeft, History, FormInput } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { ScrollArea } from "../components/ui/scroll-area";
import ViewSelector from "../components/ViewSelector";
import KanbanView from "../components/KanbanView";
import CardsView from "../components/CardsView";
import ListView from "../components/ListView";
import TicketsReasignadosTab from "./TicketsReasignadosTab";
import { toast } from "sonner";
export default function MisTicketsPage({ modulo = null, embedded = false }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attachingSupport, setAttachingSupport] = useState(false);
  const supportFileRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("mis");
  // Filtros combinables
  const [estadoFilter, setEstadoFilter] = useState("all");
  const [prioridadFilter, setPrioridadFilter] = useState("all");
  const [categoriaFilter, setCategoriaFilter] = useState("all");
  const [subCategoriaFilter, setSubCategoriaFilter] = useState("all");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [origenFilter, setOrigenFilter] = useState("all"); // creado_por_mi | asignado_a_mi | all
  const [viewMode, setViewMode] = useViewPreference("mis_tickets", "table");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [nuevoComentario, setNuevoComentario] = useState("");
  const [sendingComment, setSendingComment] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [prioridades, setPrioridades] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [formData, setFormData] = useState({
    titulo: "",
    descripcion: "",
    prioridad_id: "",
    categoria_id: "",
    archivos_adjuntos: []
  });
  // Reassignment state
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [reassignLoading, setReassignLoading] = useState(false);
  const [reassignMotivo, setReassignMotivo] = useState("");
  const [reassignUsuarioId, setReassignUsuarioId] = useState("");
  const [reassignDeptFilter, setReassignDeptFilter] = useState("");
  const [reassignUsuarios, setReassignUsuarios] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [correspondenciaLoading, setCorrespondenciaLoading] = useState(false);
  const [showDeclinarDialog, setShowDeclinarDialog] = useState(false);
  const [declinarMotivo, setDeclinarMotivo] = useState("");
  const fileInputRef = useRef(null);
  const { getAuthHeaders, user } = useAuth();
  // Cabeceras con módulo (Hallazgos Auditoria) cuando aplica
  const authH = () => {
    const base = (getAuthHeaders)();
    return modulo ? { ...base, headers: { ...base.headers, "X-Modulo": modulo } } : base;
  };

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tickets/mis-tickets`, authH());
      setTickets(res.data);
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
    fetchCategorias();
    fetchPrioridades();
    fetchDepartamentos();
  }, [fetchData]);

  const fetchCategorias = async () => {
    try {
      const res = await axios.get(`${API}/tickets/categorias`, authH());
      setCategorias(res.data);
    } catch (err) {
      console.error("Error fetching categorias:", err);
    }
  };

  const fetchPrioridades = async () => {
    try {
      const res = await axios.get(`${API}/tickets/prioridades`, authH());
      setPrioridades(res.data);
    } catch (err) {
      console.error("Error fetching prioridades:", err);
    }
  };

  const fetchDepartamentos = async () => {
    try {
      const res = await axios.get(`${API}/departamentos`, authH());
      setDepartamentos(res.data);
    } catch (err) {
      console.error("Error fetching departamentos:", err);
    }
  };

  // === Reassignment functions ===
  const openReassignDialog = async () => {
    setReassignMotivo("");
    setReassignUsuarioId("");
    setReassignDeptFilter("");
    try {
      const res = await axios.get(`${API}/tickets/usuarios-disponibles`, authH());
      setReassignUsuarios(res.data);
    } catch (err) {
      toast.error("Error al cargar usuarios");
    }
    setShowReassignDialog(true);
  };

  const filteredReassignUsuarios = reassignDeptFilter
    ? reassignUsuarios.filter(u => {
        const deptId = u.departamento_id || u.department_id || "";
        return deptId === reassignDeptFilter;
      })
    : reassignUsuarios;

  const handleReassign = async () => {
    if (!reassignUsuarioId) {
      toast.error("Debe seleccionar un usuario");
      return;
    }
    if (!reassignMotivo.trim()) {
      toast.error("El motivo es obligatorio");
      return;
    }
    setReassignLoading(true);
    try {
      const res = await axios.put(`${API}/tickets/${selectedTicket.id}/reasignar`, {
        nuevo_asignado_id: reassignUsuarioId,
        motivo: reassignMotivo.trim()
      }, authH());
      setSelectedTicket(res.data);
      toast.success("Ticket reasignado correctamente");
      setShowReassignDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al reasignar ticket");
    } finally {
      setReassignLoading(false);
    }
  };

  // Check if user can edit a specific ticket - ONLY creator can edit
  const canEditTicket = (ticket) => {
    if (!ticket || !user) return false;
    // Only creator can edit - assigned user can only view/change status
    const isCreator = ticket.user_id === user.id || ticket.created_by === user.id;
    return isCreator;
  };

  // Check if user can change ticket status - ONLY admin or assigned user
  const canChangeStatus = (ticket) => {
    if (!ticket || !user) return false;
    const role = user?.role?.toLowerCase() || "";
    const isAdmin = ["administrador", "admin", "superadmin"].includes(role);
    const isAssigned = ticket.asignado_a === user.id;
    return isAdmin || isAssigned;
  };

  // Solo los administradores pueden marcar tickets como Cerrado/Completado
  const userIsAdmin = () => ["administrador", "admin", "superadmin"].includes((user?.role || "").toLowerCase());

  // Open edit dialog
  const openEditDialog = (ticket) => {
    setEditingTicket(ticket);
    setFormData({
      titulo: ticket.titulo,
      descripcion: ticket.descripcion,
      prioridad_id: ticket.prioridad_id || "",
      categoria_id: ticket.categoria_id || "",
      archivos_adjuntos: ticket.archivos_adjuntos || []
    });
    setAttachedFiles(ticket.archivos_adjuntos || []);
    setShowEditDialog(true);
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingFile(true);
    try {
      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", file);
        
        const res = await axios.post(`${API}/tickets/upload`, formDataUpload, {
          ...authH(),
          headers: {
            ...authH().headers,
            "Content-Type": "multipart/form-data"
          }
        });
        
        setAttachedFiles(prev => [...prev, {
          url: res.data.url,
          filename: file.name
        }]);
      }
      toast.success("Archivos subidos correctamente");
    } catch (err) {
      toast.error("Error al subir archivos");
      console.error("Error uploading file:", err);
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Update ticket
  const updateTicket = async () => {
    if (!editingTicket) return;
    try {
      await axios.put(`${API}/tickets/${editingTicket.id}`, {
        ...formData,
        archivos_adjuntos: attachedFiles
      }, authH());
      toast.success("Ticket actualizado correctamente");
      setShowEditDialog(false);
      setEditingTicket(null);
      setFormData({ titulo: "", descripcion: "", prioridad_id: "", categoria_id: "", archivos_adjuntos: [] });
      setAttachedFiles([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar ticket");
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    try {
      const res = await axios.get(`${API}/tickets/${ticketId}`, authH());
      setSelectedTicket(res.data);
      setShowDetailModal(true);
    } catch (err) {
      console.error("Error fetching ticket details:", err);
      alert("Error al cargar los detalles del ticket");
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedTicket) return;
    setUpdatingStatus(true);
    try {
      await axios.put(`${API}/tickets/${selectedTicket.id}/estado?estado=${newStatus}`, {}, authH());
      // Refresh ticket details
      const res = await axios.get(`${API}/tickets/${selectedTicket.id}`, authH());
      setSelectedTicket(res.data);
      // Refresh list
      fetchData();
    } catch (err) {
      console.error("Error updating status:", err);
      alert(err.response?.data?.detail || "Error al cambiar el estado");
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Function to download all attachments as ZIP
  const downloadAllAttachments = async (ticketId, ticketNumero) => {
    try {
      const headers = authH();
      console.log("Download headers:", headers);
      
      const response = await axios.get(
        `${API}/tickets/${ticketId}/download-all-attachments`,
        {
          headers: headers.headers,
          responseType: 'blob'
        }
      );
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ticket_${ticketNumero || ticketId.substring(0, 8)}_adjuntos.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading attachments:", err);
      const errorMsg = err.response?.data instanceof Blob 
        ? "Error de autenticación - por favor recarga la página"
        : (err.response?.data?.detail || "Error al descargar los archivos");
      alert(errorMsg);
    }
  };

  const handleAddComment = async () => {
    if (!nuevoComentario.trim() || !selectedTicket) return;
    setSendingComment(true);
    try {
      await axios.post(`${API}/tickets/${selectedTicket.id}/comentarios?texto=${encodeURIComponent(nuevoComentario)}`, {}, authH());
      setNuevoComentario("");
      // Refresh ticket details
      const res = await axios.get(`${API}/tickets/${selectedTicket.id}`, authH());
      setSelectedTicket(res.data);
    } catch (err) {
      console.error("Error adding comment:", err);
      alert(err.response?.data?.detail || "Error al agregar comentario");
    } finally {
      setSendingComment(false);
    }
  };

  const getEstadoBadge = (estado) => {
    const styles = {
      abierto: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
      en_progreso: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
      pendiente: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
      resuelto: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
      completado: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
      cerrado: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
    };
    return <Badge className={`${styles[estado] || styles.abierto} border-0`}>{estado}</Badge>;
  };

  // Correspondencia Interna handlers
  const isCorrespondenciaTicket = (ticket) => ticket?.tipo === "correspondencia_interna";

  const handleRecibirCorrespondencia = async () => {
    if (!selectedTicket) return;
    setCorrespondenciaLoading(true);
    try {
      await axios.post(`${API}/correspondencia-interna/recibir/${selectedTicket.id}`, {}, authH());
      toast.success("Correspondencia recibida exitosamente");
      const res = await axios.get(`${API}/tickets/${selectedTicket.id}`, authH());
      setSelectedTicket(res.data);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al recibir correspondencia");
    } finally { setCorrespondenciaLoading(false); }
  };

  const handleDeclinarCorrespondencia = async () => {
    if (!selectedTicket || !declinarMotivo.trim()) {
      toast.error("Ingrese un motivo para declinar");
      return;
    }
    setCorrespondenciaLoading(true);
    try {
      await axios.post(`${API}/correspondencia-interna/declinar/${selectedTicket.id}`, { motivo: declinarMotivo }, authH());
      toast.success("Correspondencia declinada");
      setShowDeclinarDialog(false);
      setDeclinarMotivo("");
      const res = await axios.get(`${API}/tickets/${selectedTicket.id}`, authH());
      setSelectedTicket(res.data);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al declinar correspondencia");
    } finally { setCorrespondenciaLoading(false); }
  };

  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      return <Image className="w-4 h-4 text-green-500" />;
    }
    if (['pdf'].includes(ext)) {
      return <FileText className="w-4 h-4 text-red-500" />;
    }
    if (['doc', 'docx'].includes(ext)) {
      return <FileText className="w-4 h-4 text-blue-500" />;
    }
    if (['xls', 'xlsx'].includes(ext)) {
      return <FileText className="w-4 h-4 text-green-600" />;
    }
    return <File className="w-4 h-4 text-slate-500" />;
  };

  const canPreview = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf'].includes(ext);
  };

  const previewFile = (url, filename) => {
    const fullUrl = url.startsWith('http') ? url : `${API}${url}`;
    // Add preview=true parameter
    const previewUrl = fullUrl.includes('?') ? `${fullUrl}&preview=true` : `${fullUrl}?preview=true`;
    window.open(previewUrl, '_blank');
  };

  const downloadFile = (url, filename) => {
    const fullUrl = url.startsWith('http') ? url : `${API}${url}`;
    window.open(fullUrl, '_blank');
  };

  const filteredTickets = tickets.filter(t => {
    const q = (searchQuery || "").toLowerCase();
    const matchesSearch = !q ||
      t.titulo?.toLowerCase().includes(q) ||
      t.solicitante_nombre?.toLowerCase().includes(q) ||
      String(t.numero || "").includes(searchQuery);
    const matchesEstado = estadoFilter === "all" || t.estado === estadoFilter;
    const matchesPrioridad = prioridadFilter === "all" || (t.prioridad || "").toLowerCase() === prioridadFilter;
    const matchesCategoria = categoriaFilter === "all" || t.categoria_id === categoriaFilter;
    const matchesSubCategoria = subCategoriaFilter === "all" || (t.sub_categoria || "") === subCategoriaFilter;
    let matchesOrigen = true;
    if (origenFilter === "creado_por_mi") {
      matchesOrigen = t.created_by === user?.id || t.user_id === user?.id;
    } else if (origenFilter === "asignado_a_mi") {
      matchesOrigen = t.asignado_a === user?.id;
    }
    let matchesFechas = true;
    if (fechaDesde || fechaHasta) {
      const fecha = (t.created_at || "").slice(0, 10);
      if (fechaDesde && fecha < fechaDesde) matchesFechas = false;
      if (fechaHasta && fecha > fechaHasta) matchesFechas = false;
    }
    return matchesSearch && matchesEstado && matchesPrioridad && matchesCategoria && matchesSubCategoria && matchesOrigen && matchesFechas;
  });

  const filtrosActivos = (
    !!searchQuery || estadoFilter !== "all" || prioridadFilter !== "all" ||
    categoriaFilter !== "all" || subCategoriaFilter !== "all" || !!fechaDesde || !!fechaHasta ||
    origenFilter !== "all"
  );

  const limpiarFiltros = () => {
    setSearchQuery("");
    setEstadoFilter("all");
    setPrioridadFilter("all");
    setCategoriaFilter("all");
    setSubCategoriaFilter("all");
    setFechaDesde("");
    setFechaHasta("");
    setOrigenFilter("all");
  };

  // Determine if current user is assigned to the ticket
  const isAssignedToMe = (ticket) => ticket?.asignado_a === user?.id;
  const isCreatedByMe = (ticket) => ticket?.created_by === user?.id || ticket?.user_id === user?.id;

  const attachSupportFiles = async (fileList) => {
    if (!selectedTicket || !fileList || fileList.length === 0) return;
    const fd = new FormData();
    Array.from(fileList).forEach((f) => fd.append("files", f));
    setAttachingSupport(true);
    try {
      const auth = authH();
      await axios.post(`${API}/tickets/${selectedTicket.id}/adjuntar-archivos`, fd, {
        headers: { ...auth.headers, "Content-Type": "multipart/form-data" },
      });
      toast.success("Archivo(s) adjuntado(s) al ticket");
      const res = await axios.get(`${API}/tickets/${selectedTicket.id}`, authH());
      setSelectedTicket(res.data);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al adjuntar archivos");
    } finally {
      setAttachingSupport(false);
      if (supportFileRef.current) supportFileRef.current.value = "";
    }
  };

  return (
    <div className={embedded ? "flex" : "flex min-h-screen bg-slate-50 dark:bg-slate-900"}>
      {!embedded && <Sidebar />}
      <main className={`flex-1 p-4 md:p-8 ${embedded ? "pt-4 md:pt-4" : "pt-14 md:pt-8 ml-0 md:ml-64"} overflow-x-hidden`} data-testid="mis-tickets-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
            Mis Tickets
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Tickets creados o asignados a ti
          </p>
        </div>

        {/* Tabs: Mis Tickets | Tickets Reasignados */}
        <div className="flex gap-1 mb-6 border-b border-slate-200 dark:border-slate-700">
          <button
            onClick={() => setActiveTab("mis")}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${activeTab === "mis" ? "border-[#0C314C] text-[#0C314C] dark:border-blue-400 dark:text-blue-400" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid="mistickets-tab-mis"
          >
            Mis Tickets
          </button>
          <button
            onClick={() => setActiveTab("reasignados")}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${activeTab === "reasignados" ? "border-[#0C314C] text-[#0C314C] dark:border-blue-400 dark:text-blue-400" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid="mistickets-tab-reasignados"
          >
            Tickets Reasignados
          </button>
        </div>

        {activeTab === "reasignados" && <TicketsReasignadosTab modulo={modulo} />}

        {activeTab === "mis" && (<>
        {/* Filters & View Selector */}
        <div className="flex flex-wrap gap-3 mb-6 items-end">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Buscar</Label>
            <Search className="absolute left-3 top-[34px] w-4 h-4 text-slate-400" />
            <Input
              placeholder="Título, solicitante o número..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 mt-1"
              data-testid="search-tickets"
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Origen</Label>
            <select
              value={origenFilter}
              onChange={(e) => setOrigenFilter(e.target.value)}
              className="border rounded px-2 py-2 text-sm bg-white h-10 min-w-[160px]"
              data-testid="mistickets-filter-origen"
            >
              <option value="all">Todos</option>
              <option value="creado_por_mi">Creados por mí</option>
              <option value="asignado_a_mi">Asignados a mí</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Estado</Label>
            <select
              value={estadoFilter}
              onChange={(e) => setEstadoFilter(e.target.value)}
              className="border rounded px-2 py-2 text-sm bg-white h-10 min-w-[130px]"
              data-testid="mistickets-filter-estado"
            >
              <option value="all">Todos</option>
              <option value="abierto">Abierto</option>
              <option value="en_progreso">En Progreso</option>
              <option value="pendiente">Pendiente</option>
              <option value="resuelto">Resuelto</option>
              <option value="cerrado">Cerrado</option>
              <option value="completado">Completado</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Prioridad</Label>
            <select
              value={prioridadFilter}
              onChange={(e) => setPrioridadFilter(e.target.value)}
              className="border rounded px-2 py-2 text-sm bg-white h-10 min-w-[110px]"
              data-testid="mistickets-filter-prioridad"
            >
              <option value="all">Todas</option>
              <option value="critica">Crítica</option>
              <option value="alta">Alta</option>
              <option value="media">Media</option>
              <option value="baja">Baja</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Categoría</Label>
            <select
              value={categoriaFilter}
              onChange={(e) => { setCategoriaFilter(e.target.value); setSubCategoriaFilter("all"); }}
              className="border rounded px-2 py-2 text-sm bg-white h-10 min-w-[170px]"
              data-testid="mistickets-filter-categoria"
            >
              <option value="all">Todas</option>
              {categorias.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Sub-categoría</Label>
            <select
              value={subCategoriaFilter}
              onChange={(e) => setSubCategoriaFilter(e.target.value)}
              className="border rounded px-2 py-2 text-sm bg-white h-10 min-w-[170px]"
              data-testid="mistickets-filter-subcategoria"
            >
              <option value="all">Todas</option>
              {[...new Set(tickets.map(t => t.sub_categoria).filter(Boolean))].sort().map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Desde</Label>
            <Input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="h-10 min-w-[140px]" data-testid="mistickets-fecha-desde" />
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Hasta</Label>
            <Input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="h-10 min-w-[140px]" data-testid="mistickets-fecha-hasta" />
          </div>

          {filtrosActivos && (
            <Button
              variant="outline"
              onClick={limpiarFiltros}
              className="border-rose-300 text-rose-700 hover:bg-rose-50 h-10"
              data-testid="mistickets-limpiar-filtros"
            >
              Limpiar filtros
            </Button>
          )}

          <ViewSelector currentView={viewMode} onViewChange={setViewMode} />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : filteredTickets.length === 0 ? (
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="py-12 text-center">
              <MessageSquare className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <p className="text-slate-500 dark:text-slate-400">No tienes tickets</p>
            </CardContent>
          </Card>
        ) : viewMode === 'kanban' ? (
          <KanbanView 
            items={filteredTickets}
            columns={[
              { id: 'pendiente', label: 'Abierto / Pendiente', color: 'bg-yellow-500' },
              { id: 'en_proceso', label: 'En Progreso', color: 'bg-blue-500' },
              { id: 'completado', label: 'Resuelto', color: 'bg-green-500' },
            ]}
            statusField="estado"
            onItemClick={(ticket) => fetchTicketDetails(ticket.id)}
            renderCard={(ticket) => (
              <div className="cursor-pointer">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-500">#{ticket.numero}</span>
                  <Badge variant="outline" className="text-xs">{ticket.prioridad}</Badge>
                </div>
                <p className="font-medium text-sm text-slate-900 dark:text-white line-clamp-2">{ticket.titulo}</p>
                <p className="text-xs text-slate-500 mt-1">{ticket.solicitante_nombre}</p>
                {isAssignedToMe(ticket) && (
                  <Badge className="mt-2 bg-purple-100 text-purple-700 text-xs">Asignado a ti</Badge>
                )}
              </div>
            )}
          />
        ) : viewMode === 'cards' ? (
          <CardsView
            items={filteredTickets}
            columns={3}
            onItemClick={(ticket) => fetchTicketDetails(ticket.id)}
            renderCard={(ticket) => (
              <div className="cursor-pointer">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs">#{ticket.numero}</Badge>
                    {getEstadoBadge(ticket.estado)}
                  </div>
                  <CardTitle className="text-base mt-2">{ticket.titulo}</CardTitle>
                </CardHeader>
                <CardContent className="pb-2">
                  <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2">{ticket.descripcion}</p>
                  <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
                    <User className="w-3 h-3" />
                    <span>{ticket.solicitante_nombre}</span>
                  </div>
                  {isAssignedToMe(ticket) && (
                    <Badge className="mt-2 bg-purple-100 text-purple-700 text-xs">Asignado a ti</Badge>
                  )}
                  {ticket.archivos_adjuntos?.length > 0 && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
                      <Paperclip className="w-3 h-3" />
                      <span>{ticket.archivos_adjuntos.length} archivo(s)</span>
                    </div>
                  )}
                </CardContent>
              </div>
            )}
          />
        ) : viewMode === 'list' ? (
          <ListView
            items={filteredTickets}
            onItemClick={(ticket) => fetchTicketDetails(ticket.id)}
            renderItem={(ticket) => (
              <div className="flex items-center w-full cursor-pointer">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">#{ticket.numero}</span>
                    <p className="font-medium text-sm text-slate-900 dark:text-white truncate">{ticket.titulo}</p>
                    {isAssignedToMe(ticket) && (
                      <Badge className="bg-purple-100 text-purple-700 text-xs">Asignado</Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate mt-0.5">{ticket.solicitante_nombre}</p>
                </div>
                <div className="flex items-center gap-3 ml-4">
                  {ticket.archivos_adjuntos?.length > 0 && (
                    <Badge variant="outline" className="text-xs">
                      <Paperclip className="w-3 h-3 mr-1" />
                      {ticket.archivos_adjuntos.length}
                    </Badge>
                  )}
                  {getEstadoBadge(ticket.estado)}
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); fetchTicketDetails(ticket.id); }}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  {canEditTicket(ticket) && (
                    <Button size="sm" variant="ghost" className="text-green-600" onClick={(e) => { e.stopPropagation(); openEditDialog(ticket); }}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          />
        ) : (
          <div className="space-y-4">
            {filteredTickets.map((ticket) => (
              <Card 
                key={ticket.id} 
                className="dark:bg-slate-800 dark:border-slate-700 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => fetchTicketDetails(ticket.id)}
                data-testid={`ticket-card-${ticket.numero}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center">
                        <Ticket className="w-6 h-6 text-slate-600 dark:text-slate-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-mono text-slate-500">#{ticket.numero}</span>
                          <p className="font-medium text-slate-900 dark:text-white">{ticket.titulo}</p>
                          {isAssignedToMe(ticket) && (
                            <Badge className="bg-purple-100 text-purple-700 text-xs">Asignado a ti</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400 mt-1">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {ticket.solicitante_nombre}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(ticket.fecha_creacion).toLocaleDateString('es-ES')}
                          </span>
                          {ticket.archivos_adjuntos?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Paperclip className="w-3 h-3" />
                              {ticket.archivos_adjuntos.length} archivo(s)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getEstadoBadge(ticket.estado)}
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); fetchTicketDetails(ticket.id); }}>
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                      {canEditTicket(ticket) && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={(e) => { e.stopPropagation(); openEditDialog(ticket); }}
                          className="border-green-200 text-green-600 hover:bg-green-50"
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Ticket className="w-5 h-5" />
                Ticket #{selectedTicket?.numero}
                {selectedTicket && getEstadoBadge(selectedTicket.estado)}
              </DialogTitle>
              <DialogDescription>
                {selectedTicket?.titulo}
              </DialogDescription>
            </DialogHeader>
            
            <div className="pr-2">
              {selectedTicket && (
                <div className="space-y-6">
                  {/* Ticket Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Solicitante</Label>
                      <p className="font-medium">{selectedTicket.solicitante_nombre || 'No especificado'}</p>
                      {selectedTicket.solicitante_email && (
                        <p className="text-sm text-slate-500">{selectedTicket.solicitante_email}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Categoría</Label>
                      <p className="font-medium">{selectedTicket.categoria_name || 'Sin categoría'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Prioridad</Label>
                      <Badge 
                        style={{ backgroundColor: selectedTicket.prioridad_color || '#3b82f6' }}
                        className="text-white"
                      >
                        {selectedTicket.prioridad || 'Normal'}
                      </Badge>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Fecha de Creación</Label>
                      <p className="font-medium">
                        {new Date(selectedTicket.fecha_creacion).toLocaleString('es-ES')}
                      </p>
                    </div>
                    {selectedTicket.asignado_a_nombre && (
                      <div>
                        <Label className="text-xs text-slate-500">Asignado a</Label>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{selectedTicket.asignado_a_nombre}</p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-xs text-amber-600 border-amber-300 hover:bg-amber-50"
                            onClick={openReassignDialog}
                            data-testid="mis-tickets-reasignar-btn"
                          >
                            <ArrowRightLeft className="w-3 h-3 mr-1" />
                            Reasignar
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <Label className="text-xs text-slate-500">Descripción</Label>
                    <div className="mt-1 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="whitespace-pre-wrap">{selectedTicket.descripcion || 'Sin descripción'}</p>
                    </div>
                  </div>

                  {/* Custom Fields Data Display (clonado de Tickets) */}
                  {selectedTicket.custom_fields_data && Object.keys(selectedTicket.custom_fields_data).length > 0 && (
                    <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                      <div className="flex items-center gap-2 mb-3">
                        <FormInput className="w-4 h-4 text-[#0C314C]" />
                        <Label className="text-slate-900 dark:text-white font-medium">Campos Personalizados</Label>
                      </div>
                      <div className="grid grid-cols-2 gap-3 bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                        {(selectedTicket.custom_fields_schema || []).map((field) => {
                          const fieldKey = field.name || field.id;
                          const value = selectedTicket.custom_fields_data[fieldKey];
                          if (value === undefined || value === "" || value === null) return null;

                          const displayValue = field.field_type === "checkbox"
                            ? (value === true || value === "true" ? "Sí" : "No")
                            : field.field_type === "date" && value
                              ? new Date(value).toLocaleDateString('es-ES')
                              : value;

                          return (
                            <div key={fieldKey} className="text-sm">
                              <span className="text-slate-500 dark:text-slate-400">{field.label}: </span>
                              <span className="text-slate-900 dark:text-white font-medium">{displayValue}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Attached Files */}
                  {(selectedTicket.archivos_adjuntos?.length > 0 || selectedTicket.archivo_adjunto_url) && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs text-slate-500">
                          Archivos Adjuntos ({(selectedTicket.archivos_adjuntos?.length || 0) + (selectedTicket.archivo_adjunto_url ? 1 : 0)})
                        </Label>
                        {((selectedTicket.archivos_adjuntos?.length || 0) + (selectedTicket.archivo_adjunto_url ? 1 : 0)) > 1 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadAllAttachments(selectedTicket.id, selectedTicket.numero)}
                            className="text-green-600 border-green-300 hover:bg-green-50"
                            data-testid="download-all-attachments-btn"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Descargar Todos (ZIP)
                          </Button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {/* Legacy single file */}
                        {selectedTicket.archivo_adjunto_url && (
                          <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border">
                            <div className="flex items-center gap-3">
                              {getFileIcon(selectedTicket.archivo_adjunto_nombre)}
                              <p className="font-medium text-sm truncate max-w-[200px]">{selectedTicket.archivo_adjunto_nombre || 'Archivo adjunto'}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {canPreview(selectedTicket.archivo_adjunto_nombre) && (
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => previewFile(selectedTicket.archivo_adjunto_url, selectedTicket.archivo_adjunto_nombre)}
                                  title="Ver archivo"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => downloadFile(selectedTicket.archivo_adjunto_url, selectedTicket.archivo_adjunto_nombre)}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                        {/* Multiple attachments */}
                        {selectedTicket.archivos_adjuntos?.map((archivo, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
                          >
                            <div className="flex items-center gap-3">
                              {getFileIcon(archivo.nombre || archivo.filename || archivo.url)}
                              <div>
                                <p className="font-medium text-sm">{archivo.nombre || archivo.filename || `Archivo ${idx + 1}`}</p>
                                {archivo.size && (
                                  <p className="text-xs text-slate-500">
                                    {(archivo.size / 1024).toFixed(1)} KB
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {canPreview(archivo.nombre || archivo.filename || archivo.url) && (
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => previewFile(archivo.url, archivo.nombre || archivo.filename)}
                                  title="Ver archivo"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => downloadFile(archivo.url, archivo.nombre || archivo.filename)}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Descargar
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Correspondencia Interna Actions */}
                  {isCorrespondenciaTicket(selectedTicket) && selectedTicket.estado !== 'resuelto' && selectedTicket.estado !== 'cerrado' && (
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4" data-testid="correspondencia-actions">
                      <Label className="text-xs text-indigo-600 dark:text-indigo-400 mb-3 block font-semibold">Acciones de Correspondencia</Label>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                        Revise el reporte adjunto y confirme o decline la correspondencia.
                      </p>
                      <div className="flex gap-3">
                        <Button
                          onClick={handleRecibirCorrespondencia}
                          disabled={correspondenciaLoading}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid="recibir-correspondencia-btn"
                        >
                          {correspondenciaLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                          Recibir
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setShowDeclinarDialog(true)}
                          disabled={correspondenciaLoading}
                          className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400"
                          data-testid="declinar-correspondencia-btn"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Declinar
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Change Status — solo admin o usuario asignado */}
                  {canChangeStatus(selectedTicket) ? (
                    <div>
                      <Label className="text-xs text-slate-500 mb-2 block">Cambiar Estado</Label>
                      <div className="flex flex-wrap gap-2">
                        {['abierto', 'en_progreso', 'pendiente', 'resuelto', ...(userIsAdmin() ? ['cerrado', 'completado'] : [])].map((estado) => (
                          <Button
                            key={estado}
                            size="sm"
                            variant={selectedTicket.estado === estado ? "default" : "outline"}
                            onClick={() => handleStatusChange(estado)}
                            disabled={updatingStatus || selectedTicket.estado === estado}
                            className="capitalize"
                          >
                            {estado === 'resuelto' && <CheckCircle className="w-4 h-4 mr-1" />}
                            {estado === 'pendiente' && <AlertCircle className="w-4 h-4 mr-1" />}
                            {estado === 'cerrado' && <XCircle className="w-4 h-4 mr-1" />}
                            {estado === 'completado' && <CheckCircle2 className="w-4 h-4 mr-1" />}
                            {estado.replace('_', ' ')}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 rounded-lg p-3">
                      <AlertCircle className="w-4 h-4" />
                      <span>Solo el administrador o el usuario asignado pueden cambiar el estado del ticket.</span>
                    </div>
                  )}

                  {/* Historial de Reasignaciones */}
                  {selectedTicket.historial_reasignaciones?.length > 0 && (
                  <div data-testid="mis-tickets-historial-reasignaciones">
                    <Label className="text-xs text-slate-500 mb-2 flex items-center gap-2">
                      <History className="w-3.5 h-3.5 text-amber-600" />
                      Historial de Reasignaciones ({selectedTicket.historial_reasignaciones.length})
                    </Label>
                    <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
                      {[...selectedTicket.historial_reasignaciones].reverse().map((h, idx) => (
                        <div key={h.id || idx} className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2.5 border border-amber-200 dark:border-amber-800">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-amber-800 dark:text-amber-300">
                              {h.reasignado_por_nombre}
                            </span>
                            <span className="text-xs text-slate-500">
                              {new Date(h.fecha).toLocaleString('es-ES')}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-sm">
                            <span className="text-slate-600">{h.de_usuario_nombre}</span>
                            <ArrowRightLeft className="w-3 h-3 text-amber-500 mx-1" />
                            <span className="font-medium text-slate-800">{h.a_usuario_nombre}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1 italic">Motivo: {h.motivo}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  )}

                  {/* Comments */}
                  <div>
                    <Label className="text-xs text-slate-500 mb-2 block">
                      Comentarios ({selectedTicket.comentarios?.length || 0})
                    </Label>
                    <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
                      {selectedTicket.comentarios?.length > 0 ? (
                        selectedTicket.comentarios.map((comentario, idx) => (
                          <div 
                            key={comentario.id || idx}
                            className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">{comentario.autor}</span>
                              <span className="text-xs text-slate-500">
                                {new Date(comentario.fecha).toLocaleString('es-ES')}
                              </span>
                            </div>
                            <p className="text-sm">{comentario.texto}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-slate-500 italic">No hay comentarios aún</p>
                      )}
                    </div>

                    {/* Add Comment */}
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Escribe un comentario..."
                        value={nuevoComentario}
                        onChange={(e) => setNuevoComentario(e.target.value)}
                        className="flex-1"
                        rows={2}
                      />
                      <Button 
                        onClick={handleAddComment}
                        disabled={!nuevoComentario.trim() || sendingComment}
                      >
                        {sendingComment ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </Button>
                    </div>

                    {/* Adjuntar archivos al ticket existente (múltiples) */}
                    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                      <input
                        type="file"
                        multiple
                        ref={supportFileRef}
                        className="hidden"
                        onChange={(e) => attachSupportFiles(e.target.files)}
                        data-testid="mistickets-attach-input"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => supportFileRef.current?.click()}
                        disabled={attachingSupport}
                        data-testid="mistickets-attach-btn"
                      >
                        {attachingSupport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Paperclip className="w-4 h-4 mr-2" />}
                        Adjuntar archivo(s)
                      </Button>
                      <span className="text-[11px] text-slate-400 ml-2">Puedes seleccionar varios archivos a la vez.</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Ticket Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="w-5 h-5 text-green-600" />
                Editar Ticket #{editingTicket?.numero}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Título *</Label>
                <Input
                  value={formData.titulo}
                  onChange={(e) => setFormData({...formData, titulo: e.target.value})}
                  placeholder="Breve descripción del problema"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Descripción *</Label>
                <Textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                  placeholder="Describa el problema en detalle"
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prioridad</Label>
                  <Select value={formData.prioridad_id} onValueChange={(value) => setFormData({...formData, prioridad_id: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {prioridades.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Categoría</Label>
                  <Select value={formData.categoria_id} onValueChange={(value) => setFormData({...formData, categoria_id: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {categorias.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Archivos adjuntos */}
              <div className="space-y-2">
                <Label>Archivos Adjuntos</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    ref={fileInputRef}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFile}
                    className="w-full"
                  >
                    {uploadingFile ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Agregar archivos
                  </Button>
                </div>
                
                {attachedFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {attachedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700 rounded text-sm">
                        <span className="flex items-center gap-2 truncate">
                          <FileText className="w-4 h-4 text-blue-500" />
                          {file.filename}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setAttachedFiles(prev => prev.filter((_, i) => i !== index))}
                          className="text-red-500 h-6 w-6 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowEditDialog(false); setEditingTicket(null); setFormData({ titulo: "", descripcion: "", prioridad_id: "", categoria_id: "", archivos_adjuntos: [] }); setAttachedFiles([]); }}>
                Cancelar
              </Button>
              <Button 
                onClick={updateTicket}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Guardar Cambios
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reassign Ticket Dialog */}
        <Dialog open={showReassignDialog} onOpenChange={setShowReassignDialog}>
          <DialogContent className="max-w-md" data-testid="mis-tickets-reasignar-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5 text-amber-600" />
                Reasignar Ticket #{selectedTicket?.numero}
              </DialogTitle>
              <DialogDescription>
                Actualmente asignado a: <strong>{selectedTicket?.asignado_a_nombre}</strong>
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Filtrar por Departamento</Label>
                <SearchableSelect
                  value={reassignDeptFilter}
                  onValueChange={(v) => { setReassignDeptFilter(v); setReassignUsuarioId(""); }}
                  options={departamentos.map(d => ({ value: d.id, label: d.name || d.nombre }))}
                  allLabel="Todos los departamentos"
                  searchPlaceholder="Buscar departamento..."
                  emptyText="Departamento no encontrado."
                  data-testid="mis-tickets-reasignar-dept-select"
                />
              </div>

              <div className="space-y-2">
                <Label>Nuevo Responsable <span className="text-red-500">*</span></Label>
                <SearchableSelect
                  value={reassignUsuarioId}
                  onValueChange={setReassignUsuarioId}
                  options={filteredReassignUsuarios
                    .filter(u => u.id !== selectedTicket?.asignado_a)
                    .map(u => ({ value: u.id, label: u.name || u.email }))}
                  allLabel="Seleccionar usuario"
                  searchPlaceholder="Buscar usuario..."
                  emptyText="Usuario no encontrado."
                  data-testid="mis-tickets-reasignar-usuario-select"
                />
              </div>

              <div className="space-y-2">
                <Label>Motivo de la Reasignación <span className="text-red-500">*</span></Label>
                <Textarea
                  value={reassignMotivo}
                  onChange={(e) => setReassignMotivo(e.target.value)}
                  placeholder="Explique el motivo de la reasignación..."
                  rows={3}
                  data-testid="mis-tickets-reasignar-motivo-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReassignDialog(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleReassign}
                disabled={reassignLoading || !reassignUsuarioId || !reassignMotivo.trim()}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="mis-tickets-reasignar-confirmar-btn"
              >
                {reassignLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ArrowRightLeft className="w-4 h-4 mr-1" />}
                Confirmar Reasignación
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Declinar Correspondencia Dialog */}
        <Dialog open={showDeclinarDialog} onOpenChange={setShowDeclinarDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <XCircle className="w-5 h-5" />
                Declinar Correspondencia
              </DialogTitle>
              <DialogDescription>
                Indique el motivo por el cual declina esta correspondencia. El ticket quedará pendiente para reasignación.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Motivo <span className="text-red-500">*</span></Label>
                <Textarea
                  value={declinarMotivo}
                  onChange={(e) => setDeclinarMotivo(e.target.value)}
                  placeholder="Explique por qué declina esta correspondencia..."
                  rows={3}
                  data-testid="declinar-motivo-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowDeclinarDialog(false); setDeclinarMotivo(""); }}>Cancelar</Button>
              <Button
                onClick={handleDeclinarCorrespondencia}
                disabled={correspondenciaLoading || !declinarMotivo.trim()}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirmar-declinar-btn"
              >
                {correspondenciaLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                Confirmar Declinación
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </>)}
      </main>
    </div>
  );
}
